import React, { useState, useRef, useEffect, useMemo } from "react";
import { Button, Card, Input, Label } from "reactstrap";
import { SearchIcon } from "lucide-react";
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import CommonTable from "../CommonTable";
import SearchFilterButtons from "../SearchFilterButtons";
import AddSample from "./AddSample";
import { addGlobalSample, deleteSample, getGlobalSampleList, getSampleList, statusSample } from "./service/sample-service";
import { toast } from "react-toastify";
import { useLoader } from "../../_helper/LoaderContext";
import Select, { components } from 'react-select';

// Custom Checkbox Option
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};
// Select All Option
const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px',
        height: '44px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '44px',
        padding: '0 8px',
        overflowX: 'auto',        // This enables horizontal scroll
        overflowY: 'hidden',
        flexWrap: 'nowrap',       // Important: prevents wrapping to new line
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',   // Firefox
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,  // Prevents shrinking
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};


export default function SampleHome() {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false)
    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const [data, setData] = useState({})
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [imported, setImported] = useState("add")
    const [sample, setSample] = useState([]);

    // Global units from master list
    const [unitMaster, setUnitMaster] = useState([]);

    // Selected global units (for filtering or submission)
    const [selectedGlobalUnits, setSelectedGlobalUnits] = useState([]);

    const allOption = useMemo(() => ({ label: t("Select All"), value: "*" }), [t]);

    // Transform unitMaster into react-select format
    const unitOptions = useMemo(() => {
        const options = unitMaster.map(u => ({
            label: u.name,
            value: u._id,
            description: u.description || ""
        }));
        return unitMaster.length > 0 ? [allOption, ...options] : [];
    }, [unitMaster, allOption]);

    // Handle Select Change with "Select All" logic
    const handleGlobalUnitChange = (selected) => {
        // "selected" comes from react-select (may include "Select All")
        if (!selected) {
            setSelectedGlobalUnits([]);
            return;
        }

        const hasSelectAll = selected.some(opt => opt.value === "*");

        if (hasSelectAll) {
            // Toggle Select All
            if (selectedGlobalUnits.length === unitMaster.length) {
                setSelectedGlobalUnits([]); // Deselect all
            } else {
                // Select all real units
                setSelectedGlobalUnits(unitMaster.map(u => ({
                    label: u.name,
                    value: u._id
                })));
            }
        } else {
            // Normal multi-select behavior
            // Filter out the "Select All" option from value
            const realSelections = selected.filter(opt => opt.value !== "*");
            setSelectedGlobalUnits(realSelections);
        }
    };

    const showAllEquipment = (equipmentList = []) => {
        const names = equipmentList
            .map((item) => item?.name)
            .filter(Boolean);

        const listHtml = names
            .map(
                (name) => `
                <div style="
                    display:inline-flex;
                    align-items:center;
                    margin:4px;
                    padding:6px 12px;
                    border-radius:999px;
                    background:#eef6f1;
                    color:#0f5132;
                    font-size:13px;
                    font-weight:500;
                    border:1px solid #cfe8d8;
                ">
                    ${name}
                </div>
            `
            )
            .join("");

        Swal.fire({
            title: `<div style="font-size:18px;font-weight:600;color:#1b255b;">${t("Required Equipment")}</div>
                    <div style="font-size:13px;color:#6b7280;margin-top:4px;">${names.length} ${names.length > 1 ? t("items") : t("item")}</div>`,
            html: `<div style="display:flex;flex-wrap:wrap;justify-content:center;padding:8px 4px;">${listHtml || `<div>${t("No equipment")}</div>`}</div>`,
            confirmButtonText: t("Close"),
            confirmButtonColor: "#1b255b",
            width: "480px",
        });
    };

    const formatEquipmentDisplay = (equipmentList) => {
        if (!Array.isArray(equipmentList) || equipmentList.length === 0) {
            return <span className="tw-text-gray-400">—</span>;
        }

        const firstName = equipmentList[0]?.name || "—";
        const moreCount = equipmentList.length - 1;

        return (
            <div className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap">
                <span
                    // className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[#eef6f1] tw-text-[#0f5132] tw-border tw-border-[#cfe8d8] tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium"
                    className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[#1b255b] tw-text-[#0f5132] tw-border tw-border-[#cfe8d8] tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium"
                    title={firstName}
                >
                    {firstName}
                </span>
                {moreCount > 0 && (
                    <button
                        type="button"
                        className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-[#1b255b] tw-text-white tw-px-2.5 tw-py-1 tw-text-xs tw-font-semibold tw-border-0 hover:tw-opacity-90 tw-transition-opacity"
                        onClick={(e) => {
                            e.stopPropagation();
                            showAllEquipment(equipmentList);
                        }}
                    >
                        +{moreCount} {t("more")}
                    </button>
                )}
            </div>
        );
    };

    const columns = [
        { header: t("Sample Name"), accessor: "name", width: "40%" },
        {
            header: t("Equipment"),
            accessor: "requiredEquipment",
            width: "40%",
            render: (value) => formatEquipmentDisplay(value),
        },
    ];

    useEffect(() => {
        getList()
    }, [currentPage, entriesPerPage])

    const getList = async (query = appliedSearch) => {
        setLoading(true)
        try {
            const form_data = new FormData()
            form_data.append("search", query);
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);
            const res = await getSampleList(form_data)
            if (res.data.success == true) {
                setSample(res.data.data.data)
                setTotalEntries(res.data.data.totalCount || 0);
            }
        } catch (error) {
            console.log(error)
        } finally {
            setLoading(false)
        }
    }

    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    const handleToggleStatus = (user) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: user.isActive
                ? t("You want to inactivate this sample?")
                : t("You want to activate this sample?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: `${user.isActive == true ? t("Inactive") : t("Active")}`
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true)
                try {
                    const res = await statusSample(user._id)
                    if (res.data.success == true) {
                        toast.success(res.data.message)
                        getList()
                    }
                } catch (error) {
                    console.log(error)
                } finally {
                    setLoading(false)
                }
            }
        });
    };

    const actions = [
        { label: t("Edit"), onClick: (row) => handleEdit(row) },
        { label: t("Delete"), onClick: (row) => detele(row) },
    ]

    const handleEdit = (row) => {
        // console.log(row)
        setData(row)
        setEdit(true)
    }

    const editClose = () => {
        setEdit(false)
    }

    const detele = (user) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("You want to delete this sample?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("Yes, delete it!")
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true)
                try {
                    const res = await deleteSample(user._id)
                    if (res.data.success == true) {
                        toast.success(res.data.message)
                        getList()
                    }
                } catch (error) {
                    console.log(error)
                } finally {
                    setLoading(false)
                }
                // Swal.fire({
                //     // title: "Deleted!",
                //     text: "Your file has been updated.",
                //     icon: "success"
                // });
            }
        });
    };

    const handleSearch = () => {
        const query = search.trim();
        setAppliedSearch(query);
        if (currentPage === 1) {
            getList(query);
        } else {
            setCurrentPage(1);
        }
    };

    const handleClear = () => {
        setSearch("");
        if (!appliedSearch) return;
        setAppliedSearch("");
        if (currentPage === 1) {
            getList("");
        } else {
            setCurrentPage(1);
        }
    };

    useEffect(() => {
        getGlobalSample()
    }, [])

    // Fetch global units
    const getGlobalSample = async () => {
        setLoading(true);
        try {
            const res = await getGlobalSampleList();
            if (res.data.success) {
                setUnitMaster(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching global units:", error);
        } finally {
            setLoading(false);
        }
    };

    // Submit selected global units
    const handleSubmitSelected = async () => {
        const selectedIds = selectedGlobalUnits
            .filter(item => item.value !== "*")
            .map(item => item.value);

        if (selectedIds.length === 0) {
            toast.info(t("No units selected"));
            return;
        }
        const payload = {
            masterSampleTypeIds: selectedIds
        }
        setLoading(true)
        try {
            const res = await addGlobalSample(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                setSelectedGlobalUnits([]);
                handleClose()
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    const handleRadio = (e) => {
        const value = e.target.value;
        setImported(value);
    };

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <div className="input-group" style={{ flex: "1 1 260px", maxWidth: 460 }}>
                            <span className="input-group-text tw-bg-white" id="basic-addon1"><SearchIcon /></span>
                            <input
                                type="text"
                                className="form-control icon_form"
                                placeholder={t("Search by name")}
                                aria-label={t("Search by name")}
                                aria-describedby="basic-addon1"
                                style={{ borderLeft: 'none' }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </div>
                        <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} />
                        <div className="ms-md-auto">
                            {/* <button
                                className="btn btn-success me-2"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                ({selectedGlobalUnits.length})
                            </button> */}
                            <button className="btn btn-primary" onClick={handleShow}>
                                {t("Add")}
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <CommonTable
                                columns={columns}
                                data={sample}
                                onToggleStatus={handleToggleStatus}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}   // pass value
                                setEntriesPerPage={setEntriesPerPage}  // pass setter
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <Modal show={show} onHide={handleClose} centered size="md">
                <Modal.Header closeButton className="py-2 px-3 border-bottom">
                    <Modal.Title className="fs-6 fw-semibold m-0">{t("Add Sample")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-2 px-3 pb-0">
                    <div className="d-flex tw-space-x-4 mb-2">
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-add"
                                type="radio"
                                name="customerType"
                                value="add"
                                checked={imported === "add"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-add" className="mb-0 pe-2">
                                {t("Manual")}
                            </Label>

                        </div>
                        <div className="radio radio-primary">
                            <Input
                                id="customer-radio-gloabal"
                                type="radio"
                                name="customerType"
                                value="gloabal"
                                checked={imported === "gloabal"}
                                onChange={handleRadio}
                            />
                            <Label for="customer-radio-gloabal" className="mb-0 pe-2">
                                {t("Import From Database")}
                            </Label>
                        </div>
                        {selectedGlobalUnits.length > 0 && (
                            <button
                                className="btn btn-primary btn-sm px-3"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                {t("Import")} ({selectedGlobalUnits.length})
                            </button>
                        )}
                    </div>
                </Modal.Body>
                {imported !== "add" ? (
                    <>
                        <Modal.Body className="py-2 px-3">
                            <Select
                                isMulti
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                options={unitOptions}
                                value={selectedGlobalUnits}
                                onChange={handleGlobalUnitChange}
                                placeholder={t("Add Samples from global list...")}
                                components={{ Option }}
                                styles={customStyles}
                                classNamePrefix="react-select"
                                isClearable={true}
                            />
                        </Modal.Body>
                        {/* <Modal.Footer>
                            <button
                              className="btn btn-primary"
                                onClick={handleSubmitSelected}
                                disabled={selectedGlobalUnits.length === 0}
                            >
                                Import ({selectedGlobalUnits.length})
                            </button>

                            <Button variant="secondary" onClick={handleClose}>
                                {t("Close")}
                            </Button>
                        </Modal.Footer> */}
                    </>
                ) : (
                    <AddSample handleClose={handleClose} getList={getList} />
                )}
            </Modal>

            <Modal show={edit} onHide={editClose} centered size="md">
                <Modal.Header closeButton className="py-2 px-3 border-bottom">
                    <Modal.Title className="fs-6 fw-semibold m-0">{t("Edit Sample")}</Modal.Title>
                </Modal.Header>
                <AddSample handleClose={editClose} data={data} getList={getList} />
            </Modal>
        </>
    )
}
